import { queryOptions } from '@tanstack/react-query';

import { api } from '@/lib/api/api';
import type { User } from '@/lib/api/domains/users';
import type { UserId } from '@/lib/api/ids';
import { keys } from '@/lib/query-keys';

/*
 * The organization's user-list read (F02 R19/R20). How the UI CONSUMES the
 * `GET /entities/:id/users` transport: the queryOptions the entity-record Users
 * section and the central list both read. The list the server returns is
 * already SCOPED to what the caller's session may see — the frontend never
 * widens it (AC-1a35dc54: a non-Administrator reads within session scope); it
 * renders exactly what the BFF/Identity return.
 *
 * The entity's list is keyed by the organization id so a successful add/change/
 * deactivate (mutate → invalidate) refreshes exactly that list and no other.
 * The id is a plain string: `/entities/:id/users` is not in openapi.json yet,
 * so there is no `EntityId` brand to thread (ADR 0009), matching the mutations.
 */
export function entityUsersQuery(entityId: string) {
  return queryOptions<User[]>({
    queryKey: keys.users.listForEntity(entityId),
    queryFn: () => api.users.listForEntity(entityId),
  });
}

/*
 * The central/system user-list read (F02 R19/R37). How the UI consumes the
 * `GET /users` transport: the queryOptions the central admin user list reads.
 * The server returns ONLY the operator's own-organization users (AC-d621f792);
 * the frontend renders exactly those rows and never widens them. Both this list
 * and the entity-record list read `User.permissionSet` from the one shared
 * shape, so the set shows on both without opening the account (AC-6a84fe2f,
 * AC-c2e4df83, AC-b31e4456).
 */
export function systemUsersQuery() {
  return queryOptions<User[]>({
    queryKey: keys.users.system(),
    queryFn: () => api.users.listSystem(),
  });
}

/*
 * A single account's CURRENT state (F02 R21, AC-cd9143ad). How the UI consumes
 * the `GET /entities/:id/users/:userId` transport: the queryOptions the user
 * editor opens against, so the form is bound to WHAT IS IN FORCE — the server's
 * current name, email, role, administrator setting, active setting and
 * permission set — never a stale row cached off the list or the form's
 * defaults.
 *
 * `staleTime: 0` makes the read live: reopening the editor re-reads the account,
 * so a change made elsewhere while the editor was closed shows the moment it is
 * reopened (the demo's "reopen and see what is actually in force"). The id is
 * branded (`UserId`); the entity id is a plain string, matching the list (the
 * `/entities/:id/users` routes are not in openapi.json yet — ADR 0009).
 */
export function userQuery(entityId: string, id: UserId) {
  return queryOptions<User>({
    queryKey: keys.users.detail(entityId, id),
    queryFn: () => api.users.getUser(entityId, id),
    staleTime: 0,
  });
}
